import type { TabDef } from "./types";
import { ordenarPorNome } from "./ordem";
import { SETORES_FIXOS } from "./setores";

/** Departamentos da SERCAMP (reutilizado nos selects) — a lista fixa, em ordem alfabética. */
const DEPARTAMENTOS = ordenarPorNome(SETORES_FIXOS, (d) => d);

const NORMAS = [
  "ISO 9001",
  "ISO 14001",
  "ISO 45001",
  "ABNT NBR 5419",
  "ABNT NBR 10576",
  "IEC 61439",
  "IEC 60204",
  "NR-10",
];

/** Todas as abas do sistema. A ordem define o menu lateral. */
export const TABS: TabDef[] = [
  {
    key: "nao_conformidades",
    label: "Não Conformidades",
    icon: "alert",
    resource: "nao_conformidades",
    subtitle: "Registro de NCs — abertura, tratamento e encerramento. Use o botão IA para gerar o plano de ação.",
    aiPlano: true,
    fields: [
      { name: "codigo", label: "Código", type: "text" },
      { name: "titulo", label: "Título", type: "text", required: true },
      { name: "departamento", label: "Departamento", type: "select", options: DEPARTAMENTOS },
      { name: "gestor_setor", label: "Gestor do setor", type: "text", computed: true },
      { name: "tipo", label: "Tipo", type: "select", options: ["Produto", "Processo", "Sistema", "Cliente", "Fornecedor"] },
      { name: "origem", label: "Origem", type: "select", options: ["Auditoria", "Inspeção", "Reclamação", "Processo", "Cliente"] },
      { name: "gravidade", label: "Gravidade", type: "select", options: ["Baixa", "Média", "Alta", "Crítica"] },
      { name: "reincidente", label: "Reincidente", type: "select", options: ["Não", "Sim"] },
      { name: "responsavel", label: "Responsável", type: "text" },
      { name: "data_abertura", label: "Abertura", type: "date" },
      { name: "data_encerramento", label: "Encerramento", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["Aberta", "Em análise", "Em tratamento", "Encerrada"] },
      { name: "descricao", label: "Descrição", type: "textarea" },
    ],
  },
  {
    key: "acoes_corretivas",
    label: "Ações Corretivas",
    icon: "wrench",
    resource: "acoes_corretivas",
    subtitle: "Ações corretivas e preventivas vinculadas às NCs",
    fields: [
      { name: "nc_id", label: "Não Conformidade", type: "select", ref: "nao_conformidades", required: true },
      { name: "gestor_setor", label: "Gestor do setor", type: "text", computed: true },
      { name: "descricao", label: "Ação", type: "textarea", required: true },
      { name: "tipo", label: "Tipo", type: "select", options: ["Corretiva", "Preventiva", "Imediata"] },
      { name: "responsavel", label: "Responsável", type: "text" },
      { name: "prazo", label: "Prazo", type: "date" },
      { name: "data_conclusao", label: "Conclusão", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["Pendente", "Em andamento", "Concluída", "Atrasada"] },
      { name: "eficacia", label: "Eficácia", type: "select", options: ["Em avaliação", "Eficaz", "Não eficaz"] },
    ],
  },
  {
    key: "auditorias",
    label: "Auditorias",
    icon: "clipboardCheck",
    resource: "auditorias",
    subtitle: "Auditorias internas, externas e de certificação",
    fields: [
      { name: "titulo", label: "Título", type: "text", required: true },
      { name: "departamento", label: "Departamento", type: "select", options: DEPARTAMENTOS },
      { name: "gestor_setor", label: "Gestor do setor", type: "text", computed: true },
      { name: "tipo", label: "Tipo", type: "select", options: ["Interna", "Externa", "Certificação"] },
      { name: "norma", label: "Norma", type: "select", options: NORMAS },
      { name: "data", label: "Data", type: "date" },
      { name: "auditor", label: "Auditor", type: "text" },
      { name: "resultado", label: "Resultado", type: "select", options: ["Sem ressalvas", "Com ressalvas", "Não conforme"] },
      { name: "ncs_encontradas", label: "NCs encontradas", type: "number" },
      { name: "pontuacao", label: "Pontuação (0-100)", type: "number" },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  {
    key: "satisfacao_cliente",
    label: "Satisfação do Cliente",
    icon: "smile",
    resource: "satisfacao_cliente",
    subtitle: "Pesquisas de satisfação e NPS dos clientes",
    // Cliente avaliado é cadastro por nome: a lista abre em ordem alfabética.
    ordenarPor: "cliente",
    fields: [
      { name: "cliente", label: "Cliente", type: "text", required: true },
      { name: "departamento", label: "Departamento", type: "select", options: DEPARTAMENTOS },
      { name: "gestor_setor", label: "Gestor do setor", type: "text", computed: true },
      { name: "periodo", label: "Período", type: "text" },
      { name: "nota", label: "Nota (0-10)", type: "number" },
      { name: "nps", label: "NPS", type: "number" },
      { name: "canal", label: "Canal", type: "select", options: ["Pesquisa", "NPS", "E-mail", "Telefone", "Presencial"] },
      { name: "recomendaria", label: "Recomendaria?", type: "select", options: ["Sim", "Não", "Talvez"] },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  {
    key: "retrabalho",
    label: "Retrabalho",
    icon: "repeat",
    resource: "retrabalho",
    subtitle: "Horas e custo de retrabalho — base da taxa de retrabalho",
    fields: [
      { name: "descricao", label: "Descrição", type: "text", required: true },
      { name: "departamento", label: "Departamento", type: "select", options: DEPARTAMENTOS },
      { name: "gestor_setor", label: "Gestor do setor", type: "text", computed: true },
      { name: "os_referencia", label: "OS / Referência", type: "text" },
      { name: "data", label: "Data", type: "date" },
      { name: "horas_totais", label: "Horas totais", type: "number" },
      { name: "horas_retrabalho", label: "Horas de retrabalho", type: "number" },
      { name: "custo", label: "Custo", type: "currency" },
      { name: "motivo", label: "Motivo", type: "textarea" },
    ],
  },
  {
    // Ponto de integração com a execução: as duas abas acima registram o
    // indicador do DEPARTAMENTO; esta mostra cada SERVIÇO EXECUTADO e a média
    // por colaborador e por equipe, lida da Gestão da Operação/Planejamento.
    key: "desempenho",
    label: "Satisfação e Retrabalho por Equipe",
    icon: "users",
    resource: "desempenho",
    subtitle:
      "Satisfação de cada serviço executado e retrabalho por responsável — média por colaborador e por equipe, vinda da Gestão da Operação / Planejamento",
    custom: "desempenho",
    fields: [],
  },
  {
    key: "conformidade_normas",
    label: "Conformidade (Normas)",
    icon: "certificate",
    resource: "conformidade_normas",
    subtitle: "Conformidade com ISO / ABNT / IEC por departamento",
    fields: [
      { name: "norma", label: "Norma", type: "select", options: NORMAS, required: true },
      { name: "departamento", label: "Departamento", type: "select", options: DEPARTAMENTOS },
      { name: "gestor_setor", label: "Gestor do setor", type: "text", computed: true },
      { name: "requisito", label: "Requisito", type: "text" },
      { name: "status", label: "Status", type: "select", options: ["Conforme", "Parcial", "Não conforme", "Não aplicável"] },
      { name: "percentual", label: "Conformidade (%)", type: "number" },
      { name: "data_avaliacao", label: "Avaliação", type: "date" },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  {
    key: "documentos",
    label: "Documentos",
    icon: "fileText",
    resource: "documentos",
    subtitle:
      "Controle de documentos — anexe o arquivo, escolha quem aprova e envie. Depois da aprovação, a Qualidade publica e ele vira a versão vigente.",
    aprovacao: true,
    // Versão, status e data da revisão são do FLUXO (mostram a versão vigente):
    // aparecem na lista, mas não se editam — senão daria para marcar "Vigente"
    // sem aprovação. O setor é obrigatório (dele saem os sugeridos para aprovar
    // e quem publica) e "Quem aprova" também: quem cadastra faz a elaboração e
    // a revisão e escolhe a pessoa que aprova (pedido de 22/10/2026).
    // Tipo, setor e quem aprova vêm da configuração (tipos cadastrados e
    // cadastro do RH): as opções abaixo só valem enquanto ela não chega (ver
    // CrudView). Código em branco = o servidor dá a sigla do tipo + o próximo número.
    fields: [
      { name: "codigo", label: "Código", type: "text", placeholder: "Automático ao salvar" },
      { name: "titulo", label: "Título", type: "text" },
      { name: "tipo", label: "Tipo", type: "select", required: true, options: ["Política", "Manual", "Procedimento", "Instrução", "Formulário", "Registro"] },
      { name: "versao", label: "Versão vigente", type: "text", computed: true },
      { name: "departamento", label: "Setor", type: "select", options: DEPARTAMENTOS, required: true },
      { name: "gestor_setor", label: "Gestor do setor", type: "text", computed: true },
      { name: "status", label: "Status", type: "text", computed: true },
      { name: "etapa_aprovacao", label: "Aprovação", type: "text", computed: true },
      { name: "data_revisao", label: "Data da revisão", type: "date", computed: true },
      { name: "proxima_revisao", label: "Próxima revisão", type: "date", computed: true },
      { name: "responsavel", label: "Responsável", type: "text" },
      { name: "aprovador", label: "Quem aprova", type: "select", required: true, options: [] },
    ],
  },
  {
    key: "gestores_setor",
    label: "Gestores por Setor",
    icon: "users",
    resource: "gestores_setor",
    subtitle:
      "Gestores vindos do cadastro do RH, com nome, cargo, e-mail e telefone — a Qualidade registra aqui a situação de cada setor (Conforme / Não conformidade)",
    // O cadastro é do RH: a tela lista de lá e grava só a avaliação. Por isso
    // não usa o CRUD genérico (que criaria gestor à mão e duplicaria a fonte).
    custom: "gestores_setor",
    fields: [],
  },
  // A aba "Usuários" foi REMOVIDA: a criação de logins é centralizada no Portal
  // SERCAMP (lá se define o usuário e quais sistemas ele pode acessar). Criar
  // login aqui gerava conta que o portão de acesso do Portal não reconhecia.
  // O acesso passa a ser sempre pelo card do Portal (SSO). Ver server/auth.ts.
  {
    /* Aba de todos os sistemas: o pedido cai na fila do Desenvolvimento. */
    key: "solicitar_atualizacao",
    label: "Solicitar Atualização",
    icon: "refresh",
    resource: "solicitar_atualizacao",
    subtitle:
      "Marque uma reunião com o Desenvolvimento, peça um ajuste ou avise um erro. O pedido vai direto para quem cuida dos sistemas, com o seu nome e a data.",
    fields: [],
  },
];
